'use client';

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, ExternalLink, Cpu, AlertTriangle, CheckCircle, Activity, FileText, ChevronDown, ChevronUp, RotateCcw, Play, Sparkles, RefreshCw, ShieldAlert, Zap, Mail, Loader2, Check } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { useIncidentAnalysis } from '@/hooks/useIncidentAnalysis';

interface Incident {
  id: string;
  title: string;
  severity: 'p1' | 'p2' | 'p3' | 'p4';
  type: string;
  node: string;
  region: string;
  aiClassification: string;
  aiConfidence: number;
  status: 'detected' | 'classified' | 'remediating' | 'verifying' | 'resolved' | 'closed';
  slaDeadline: string;
  slaBreaching: boolean;
  snoTicket: string | null;
  remediationStatus: 'none' | 'running' | 'success' | 'failed';
  assignee: string;
  detectedAt: string;
  customer: string;
}

interface IncidentDetailPanelProps {
  incident: Incident;
  onClose: () => void;
}

const timeline = [
  { id: 'tl-1', time: '09:14:22', event: 'Incident detected by AI telemetry correlation engine', type: 'detection' },
  { id: 'tl-2', time: '09:14:35', event: 'AI classification complete — BGP_SESSION_FLAP (97% confidence)', type: 'ai' },
  { id: 'tl-3', time: '09:14:40', event: 'ServiceNow ticket INC0047821 auto-created', type: 'itsm' },
  { id: 'tl-4', time: '09:14:45', event: 'Automation rule BGP-Session-Restart triggered', type: 'automation' },
  { id: 'tl-5', time: '09:15:02', event: 'Remediation job JOB-9871 started on CORE-NYC-01', type: 'automation' },
  { id: 'tl-6', time: '09:16:14', event: 'BGP session restart executed — awaiting peer re-establishment', type: 'automation' },
  { id: 'tl-7', time: '09:23:55', event: 'Verification in progress — monitoring BGP peer count', type: 'verifying' },
];

const typeStyle: Record<string, { color: string; dot: string }> = {
  detection: { color: 'text-warning', dot: 'bg-warning' },
  ai: { color: 'text-primary', dot: 'bg-primary' },
  itsm: { color: 'text-info', dot: 'bg-info' },
  automation: { color: 'text-success', dot: 'bg-success' },
  verifying: { color: 'text-primary', dot: 'bg-primary' },
};

function ConfidenceBar({ score, color = 'bg-primary' }: { score: number; color?: string }) {
  const barColor =
    score >= 85 ? 'bg-success' : score >= 65 ? 'bg-primary' : score >= 40 ? 'bg-warning' : 'bg-danger';
  return (
    <div className="h-1.5 bg-surface-overlay rounded-full overflow-hidden">
      <div className={`h-full ${barColor} rounded-full transition-all duration-700`} style={{ width: `${score}%` }} />
    </div>
  );
}

async function sendIncidentEmail(payload: {
  type: 'incident_alert' | 'sla_breach';
  incidentRef: string;
  incidentTitle: string;
  incidentSeverity: string;
  incidentType: string;
  nodeName: string;
  region: string;
  aiClassification: string;
  aiConfidence: number;
  assignee: string;
  customer: string;
  slaDeadline?: string;
}) {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) return { success: false, error: 'Supabase not configured' };
    const response = await fetch(`${supabaseUrl}/functions/v1/send-automation-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseKey}` },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return response.ok ? { success: true, ...data } : { success: false, error: data.error };
  } catch (err: unknown) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export default function IncidentDetailPanel({ incident, onClose }: IncidentDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'ai' | 'timeline' | 'remediation'>('overview');
  const [rcaExpanded, setRcaExpanded] = useState(true);
  const { analysis, isLoading: aiLoading, error: aiError, analyzeIncident } = useIncidentAnalysis();
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<'incident_alert' | 'sla_breach' | null>(null);
  const [emailSent, setEmailSent] = useState<'incident_alert' | 'sla_breach' | null>(null);

  useEffect(() => {
    if (aiError) toast.error(`AI Analysis failed: ${aiError}`);
  }, [aiError]);

  // Auto-analyze when AI tab is opened for the first time
  useEffect(() => {
    if (activeTab === 'ai' && !hasAnalyzed && !aiLoading) {
      setHasAnalyzed(true);
      analyzeIncident({
        id: incident.id,
        title: incident.title,
        type: incident.type,
        severity: incident.severity,
        node: incident.node,
        region: incident.region,
        detectedAt: incident.detectedAt,
        customer: incident.customer,
        status: incident.status,
      });
    }
  }, [activeTab, hasAnalyzed, aiLoading, incident, analyzeIncident]);

  const handleReanalyze = () => {
    setHasAnalyzed(true);
    analyzeIncident({
      id: incident.id,
      title: incident.title,
      type: incident.type,
      severity: incident.severity,
      node: incident.node,
      region: incident.region,
      detectedAt: incident.detectedAt,
      customer: incident.customer,
      status: incident.status,
    });
  };

  const handleNotifyIncident = async (type: 'incident_alert' | 'sla_breach') => {
    setSendingEmail(type);
    const result = await sendIncidentEmail({
      type,
      incidentRef: incident.id,
      incidentTitle: incident.title,
      incidentSeverity: incident.severity,
      incidentType: incident.type,
      nodeName: incident.node,
      region: incident.region,
      aiClassification: incident.aiClassification,
      aiConfidence: incident.aiConfidence,
      assignee: incident.assignee,
      customer: incident.customer,
      slaDeadline: incident.slaDeadline,
    });
    setSendingEmail(null);
    if (result.success) {
      setEmailSent(type);
      toast.success(type === 'sla_breach' ? 'SLA breach alert sent to team' : 'Incident alert sent to team');
      setTimeout(() => setEmailSent(null), 4000);
    } else {
      toast.error(`Failed to send email: ${result.error}`);
    }
  };

  const confidenceColor =
    (analysis?.confidenceScore ?? incident.aiConfidence) >= 85
      ? 'text-success'
      : (analysis?.confidenceScore ?? incident.aiConfidence) >= 65
      ? 'text-primary' :'text-warning';

  return (
    <div className="w-[420px] flex-shrink-0 bg-surface border border-border rounded-xl flex flex-col animate-slide-in-right max-h-[calc(100vh-140px)] sticky top-0">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs font-bold text-primary">{incident.id}</span>
            <StatusBadge status={incident.severity} />
            <StatusBadge status={incident.status} />
          </div>
          <p className="text-sm font-semibold text-foreground leading-snug">{incident.title}</p>
          {incident.slaBreaching && (
            <div className="flex items-center gap-1 mt-1.5 text-danger">
              <AlertTriangle size={11} />
              <span className="text-[10px] font-bold uppercase tracking-wide">SLA breach imminent — {incident.slaDeadline}</span>
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-surface-raised text-muted hover:text-foreground transition-colors flex-shrink-0 ml-2"
        >
          <X size={15} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border px-4">
        {(['overview', 'ai', 'timeline', 'remediation'] as const).map((tab) => (
          <button
            key={`tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2.5 text-[11px] font-semibold capitalize transition-colors border-b-2 -mb-px flex items-center gap-1
              ${activeTab === tab ? 'text-primary border-primary' : 'text-muted border-transparent hover:text-foreground'}`}
          >
            {tab === 'ai' ? (
              <>
                <Sparkles size={10} />
                AI Analysis
              </>
            ) : (
              tab.charAt(0).toUpperCase() + tab.slice(1)
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Node', value: incident.node },
                { label: 'Region', value: incident.region },
                { label: 'Type', value: incident.type },
                { label: 'Customer', value: incident.customer },
                { label: 'Assignee', value: incident.assignee },
                { label: 'Detected At', value: incident.detectedAt },
              ].map((f) => (
                <div key={`field-${f.label}`} className="bg-surface-raised rounded-lg p-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted mb-0.5">{f.label}</p>
                  <p className="text-xs font-medium text-foreground font-mono">{f.value}</p>
                </div>
              ))}
            </div>

            {incident.snoTicket && (
              <div className="bg-info/10 border border-info/20 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={13} className="text-info" />
                    <span className="text-xs font-semibold text-info">ServiceNow Ticket</span>
                  </div>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="flex items-center gap-1 text-[11px] text-primary hover:underline font-mono"
                  >
                    {incident.snoTicket}
                    <ExternalLink size={10} />
                  </a>
                </div>
                <p className="text-[11px] text-info/70 mt-1">Auto-created · Priority: {incident.severity.toUpperCase()} · Status syncing</p>
              </div>
            )}

            {/* Quick AI confidence preview */}
            <div className="bg-surface-raised border border-border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Cpu size={12} className="text-primary" />
                  <span className="text-[11px] font-semibold text-foreground">AI Pre-Classification</span>
                </div>
                <span className={`font-mono text-sm font-bold ${confidenceColor}`}>{incident.aiConfidence}%</span>
              </div>
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="font-mono text-foreground-muted">{incident.aiClassification}</span>
              </div>
              <ConfidenceBar score={incident.aiConfidence} />
              <button
                onClick={() => setActiveTab('ai')}
                className="mt-2 text-[10px] text-primary hover:underline flex items-center gap-1"
              >
                <Sparkles size={9} />
                Run deep AI analysis →
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => toast.info(`Reassigning ${incident.id}…`)}
                className="flex-1 text-xs bg-surface-raised border border-border rounded-lg py-2 text-foreground hover:border-primary/40 transition-colors font-medium"
              >
                Reassign
              </button>
              <button
                onClick={() => toast.success(`${incident.id} escalated to P1`)}
                className="flex-1 text-xs bg-danger/15 border border-danger/30 rounded-lg py-2 text-danger hover:bg-danger/25 transition-colors font-medium"
              >
                Escalate
              </button>
              <button
                onClick={() => toast.success(`${incident.id} marked resolved`)}
                className="flex-1 text-xs bg-success/15 border border-success/30 rounded-lg py-2 text-success hover:bg-success/25 transition-colors font-medium"
              >
                Resolve
              </button>
            </div>

            {/* Email notification buttons */}
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => handleNotifyIncident('incident_alert')}
                disabled={sendingEmail === 'incident_alert'}
                className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-surface-raised border border-border rounded-lg py-2 text-foreground-muted hover:border-primary/40 hover:text-primary transition-colors font-medium disabled:opacity-50"
                title="Send incident alert email to team"
              >
                {sendingEmail === 'incident_alert' ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : emailSent === 'incident_alert' ? (
                  <Check size={11} className="text-success" />
                ) : (
                  <Mail size={11} />
                )}
                {emailSent === 'incident_alert' ? 'Alert Sent!' : 'Notify Team'}
              </button>
              {incident.slaBreaching && (
                <button
                  onClick={() => handleNotifyIncident('sla_breach')}
                  disabled={sendingEmail === 'sla_breach'}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-danger/10 border border-danger/30 rounded-lg py-2 text-danger hover:bg-danger/20 transition-colors font-medium disabled:opacity-50"
                  title="Send SLA breach alert email to team"
                >
                  {sendingEmail === 'sla_breach' ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : emailSent === 'sla_breach' ? (
                    <Check size={11} />
                  ) : (
                    <AlertTriangle size={11} />
                  )}
                  {emailSent === 'sla_breach' ? 'SLA Alert Sent!' : 'SLA Alert'}
                </button>
              )}
            </div>
          </>
        )}

        {activeTab === 'ai' && (
          <>
            {/* AI Analysis Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Sparkles size={12} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">OpenAI Analysis</p>
                  <p className="text-[10px] text-muted">GPT-4.1 Mini · Real-time classification</p>
                </div>
              </div>
              <button
                onClick={handleReanalyze}
                disabled={aiLoading}
                className="flex items-center gap-1.5 text-[11px] text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                title="Re-analyze with AI"
              >
                <RefreshCw size={11} className={aiLoading ? 'animate-spin' : ''} />
                {aiLoading ? 'Analyzing…' : 'Re-analyze'}
              </button>
            </div>

            {/* Loading state */}
            {aiLoading && !analysis && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <p className="text-xs text-primary font-medium">AI is analyzing incident…</p>
                <p className="text-[10px] text-muted text-center">Classifying type, severity, root cause and generating confidence score</p>
              </div>
            )}

            {/* AI Analysis Results */}
            {analysis && (
              <>
                {/* Confidence Score + Classification */}
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Cpu size={14} className="text-primary" />
                      <p className="text-xs font-semibold text-primary">AI Classification Result</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-mono text-xl font-bold ${confidenceColor}`}>{analysis.confidenceScore}%</span>
                      <span className="text-[10px] text-muted">confidence</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-foreground-muted">Incident Type</span>
                      <span className="font-mono font-semibold text-foreground text-right max-w-[180px] truncate">{analysis.incidentType}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-foreground-muted">AI Severity</span>
                      <span className={`font-semibold font-mono ${
                        analysis.severity === 'Critical' ? 'text-danger' :
                        analysis.severity === 'High' ? 'text-warning' :
                        analysis.severity === 'Medium' ? 'text-info' : 'text-success'
                      }`}>{analysis.severity}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-foreground-muted">Model</span>
                      <span className="font-mono text-foreground">GPT-4.1 Mini</span>
                    </div>
                  </div>
                  {/* Confidence bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-[10px] text-muted mb-1">
                      <span>AI Confidence Score</span>
                      <span className={confidenceColor}>{analysis.confidenceScore}%</span>
                    </div>
                    <ConfidenceBar score={analysis.confidenceScore} />
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-surface-raised border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert size={12} className="text-foreground-muted" />
                    <span className="text-[11px] font-semibold text-foreground">Executive Summary</span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-relaxed">{analysis.summary}</p>
                </div>

                {/* RCA */}
                <div className="border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setRcaExpanded(!rcaExpanded)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-surface-raised hover:bg-surface-overlay transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Activity size={13} className="text-primary" />
                      <span className="text-xs font-semibold text-foreground">AI Root Cause Analysis</span>
                    </div>
                    {rcaExpanded ? <ChevronUp size={13} className="text-muted" /> : <ChevronDown size={13} className="text-muted" />}
                  </button>
                  {rcaExpanded && (
                    <div className="p-4 space-y-3">
                      <p className="text-[11px] text-foreground-muted leading-relaxed">{analysis.rootCause}</p>
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted">Contributing factors</p>
                        {analysis.contributingFactors.map((f, idx) => (
                          <div key={`ai-factor-${idx}`} className="flex items-center gap-2 text-[11px]">
                            <div className="flex-1">
                              <div className="flex justify-between mb-0.5">
                                <span className="text-foreground-muted">{f.factor}</span>
                                <span className="font-mono text-primary">{f.weight}%</span>
                              </div>
                              <div className="h-1 bg-surface-overlay rounded-full overflow-hidden">
                                <div className="h-full bg-primary/60 rounded-full" style={{ width: `${f.weight}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="bg-warning/10 border border-warning/20 rounded-lg p-2.5 text-[11px]">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Zap size={11} className="text-warning" />
                          <p className="text-warning font-semibold">Recommended action</p>
                        </div>
                        <p className="text-foreground-muted">{analysis.recommendedAction}</p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Fallback: static classification when no AI analysis yet */}
            {!analysis && !aiLoading && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Cpu size={14} className="text-primary" />
                    <p className="text-xs font-semibold text-primary">Pre-Classification</p>
                  </div>
                  <span className="font-mono text-lg font-bold text-primary">{incident.aiConfidence}%</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground-muted">Classification</span>
                    <span className="font-mono font-semibold text-foreground">{incident.aiClassification}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground-muted">Model</span>
                    <span className="font-mono text-foreground">netops-classifier-v3.2</span>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-muted mb-1">
                    <span>Confidence</span>
                    <span>{incident.aiConfidence}%</span>
                  </div>
                  <ConfidenceBar score={incident.aiConfidence} />
                </div>
                <button
                  onClick={handleReanalyze}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs bg-primary/15 border border-primary/30 rounded-lg py-2 text-primary hover:bg-primary/25 transition-colors font-medium"
                >
                  <Sparkles size={11} />
                  Run OpenAI Deep Analysis
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-0">
            {timeline.map((event, idx) => {
              const style = typeStyle[event.type] ?? typeStyle.detection;
              return (
                <div key={event.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${style.dot}`} />
                    {idx < timeline.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                  </div>
                  <div className="pb-4 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-[10px] text-muted">{event.time}</span>
                      <span className={`text-[9px] font-semibold uppercase ${style.color}`}>{event.type}</span>
                    </div>
                    <p className="text-[11px] text-foreground-muted leading-snug">{event.event}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'remediation' && (
          <>
            <div className={`rounded-lg p-4 border ${
              incident.remediationStatus === 'success' ? 'bg-success/10 border-success/20' :
              incident.remediationStatus === 'running' ? 'bg-primary/10 border-primary/20' :
              incident.remediationStatus === 'failed' ? 'bg-danger/10 border-danger/20' : 'bg-surface-raised border-border'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Cpu size={14} className={
                    incident.remediationStatus === 'success' ? 'text-success' :
                    incident.remediationStatus === 'running' ? 'text-primary' :
                    incident.remediationStatus === 'failed' ? 'text-danger' : 'text-muted'
                  } />
                  <span className="text-xs font-semibold text-foreground">Remediation Status</span>
                </div>
                <StatusBadge status={
                  incident.remediationStatus === 'none' ? 'queued' :
                  incident.remediationStatus as any
                } />
              </div>
              {incident.remediationStatus !== 'none' && (
                <div className="space-y-1.5 text-[11px]">
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">Rule triggered</span>
                    <span className="font-mono text-foreground">BGP-Session-Restart</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">Job ID</span>
                    <span className="font-mono text-primary">JOB-9871</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">Started</span>
                    <span className="font-mono text-foreground">09:14:45</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">Duration</span>
                    <span className="font-mono text-foreground">9m 10s (ongoing)</span>
                  </div>
                </div>
              )}
            </div>

            {incident.remediationStatus === 'running' && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted">Execution steps</p>
                {[
                  { step: 'Pre-flight checks', done: true },
                  { step: 'BGP process soft-reset', done: true },
                  { step: 'Peer re-establishment wait', done: false },
                  { step: 'Route table verification', done: false },
                  { step: 'Traffic normalization check', done: false },
                ].map((s, i) => (
                  <div key={`step-${i}`} className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      s.done ? 'bg-success/20' : 'bg-surface-overlay border border-border'
                    }`}>
                      {s.done && <CheckCircle size={10} className="text-success" />}
                      {!s.done && i === 2 && <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                    </div>
                    <span className={`text-[11px] ${s.done ? 'text-foreground-muted line-through' : i === 2 ? 'text-primary font-medium' : 'text-muted'}`}>
                      {s.step}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              {incident.remediationStatus === 'none' && (
                <button
                  onClick={() => toast.info('Triggering manual remediation…')}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-primary/15 border border-primary/30 rounded-lg py-2 text-primary hover:bg-primary/25 transition-colors font-medium"
                >
                  <Play size={11} />
                  Trigger Remediation
                </button>
              )}
              {(incident.remediationStatus === 'failed' || incident.remediationStatus === 'success') && (
                <button
                  onClick={() => toast.info('Re-running remediation job…')}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-surface-raised border border-border rounded-lg py-2 text-foreground hover:border-primary/40 transition-colors font-medium"
                >
                  <RotateCcw size={11} />
                  Re-run Job
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}